import { useMemo, useEffect, useCallback } from 'react';
import { ArrowLeftSolidIcon, ArrowRightSolidIcon } from '@navi/web-ui/lib/icons';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'src/store';
import styles from '../index.module.scss';
import {
  setCurrentCasePointer,
  setNextCasesToCurrent,
  setPrevCasesToCurrent
} from 'src/pages/Cases/reducers/CasesSlice';
import { interpolatePathParams } from 'src/utils/interpolate';
import APP_ROUTES from 'src/layout/Routes';
import { SETTER_FUNCTION, fetchAllCases } from 'src/pages/Cases/CasesActions';
import { LOCAL_STORAGE_KEYS } from 'src/constants/StorageKeys';
import { AllCasesPageRequest } from 'src/pages/Cases/constants/CasesInterfaces';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import cx from 'classnames';

const NextPreviousCaseActions = () => {
  const currentCasePointer = useSelector(
    (state: RootState) => state.allCases.casesTable.currentCasePointer
  );
  const casesList = useSelector((state: RootState) => state.allCases.casesTable.casesData);
  const pagination = useSelector((state: RootState) => state.allCases.casesTable.pagination);
  const nextCasesList = useSelector((state: RootState) => state.allCases.casesTable.nextCasesData);
  const prevCasesList = useSelector((state: RootState) => state.allCases.casesTable.prevCasesData);
  const { loanId = '', customerId = '' } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allocatedCasesPageRequest = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_KEYS.ALLOCATED_CASES_PAGE_REQUEST) || '{}'
  ) as AllCasesPageRequest;

  // Fetch previous cases list if current case index is 0 and previous cases list is empty
  const getPrevCasesList = (caseIndex: number, updateParams = true) => {
    if (casesList && caseIndex === 0 && pagination.pageNo > 0) {
      dispatch(
        fetchAllCases(
          { ...allocatedCasesPageRequest, pageNo: pagination.pageNo - 1 },
          loanId,
          customerId,
          SETTER_FUNCTION.PREV,
          updateParams
        )
      );
    }
  };

  // Fetch next cases list if current case index is last and next cases list is empty
  const getNextCasesList = (caseIndex: number, updateParams = true) => {
    if (
      casesList &&
      caseIndex === casesList.length - 1 &&
      pagination.pageNo < pagination.totalPages - 1
    ) {
      dispatch(
        fetchAllCases(
          { ...allocatedCasesPageRequest, pageNo: pagination.pageNo + 1 },
          loanId,
          customerId,
          SETTER_FUNCTION.NEXT,
          updateParams
        )
      );
    }
  };

  // Finding the current case index
  const { currentCaseIndex } = useMemo(() => {
    if (casesList) {
      const currentCaseIndex = casesList?.findIndex(
        collectionCase =>
          collectionCase.customerId === customerId && collectionCase.accountNumber === loanId
      );
      getPrevCasesList(currentCaseIndex, false);
      getNextCasesList(currentCaseIndex, false);
      return { currentCaseIndex };
    }
    return { currentCaseIndex: -1 };
  }, [casesList, loanId, customerId]);

  // Disabling the previous btn if on first case of first page
  const disablePreviousButton = currentCaseIndex === 0 && pagination.pageNo === 0;

  // Disabling the next btn if on last case of last page
  const disableNextButton =
    (casesList &&
      currentCaseIndex === casesList.length - 1 &&
      !!pagination.totalPages &&
      pagination.pageNo === pagination.totalPages - 1) ||
    false;

  const onGoToPreviousCase = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CaseDetails_NavigationClick, {
      navigateType: SETTER_FUNCTION.PREV
    });
    if (currentCaseIndex != undefined && casesList) {
      if (currentCaseIndex > 0) {
        dispatch(setCurrentCasePointer(currentCaseIndex - 1));
        navigate(
          interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
            selectedTab: 'overview',
            loanId: casesList[currentCaseIndex - 1]?.accountNumber,
            customerId: casesList[currentCaseIndex - 1]?.customerId,
            caseReferenceId: casesList[currentCaseIndex - 1]?.caseReferenceId || '',
            caseBusinessVertical: casesList[currentCaseIndex - 1]?.businessVertical || ''
          })
        );
      } else if (pagination.pageNo - 1 >= 0) {
        getPrevCasesList(currentCaseIndex);
        dispatch(setPrevCasesToCurrent());
        prevCasesList &&
          navigate(
            interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
              selectedTab: 'overview',
              loanId: prevCasesList[prevCasesList.length - 1]?.accountNumber || '',
              customerId: prevCasesList[prevCasesList.length - 1]?.customerId || '',
              caseReferenceId: prevCasesList[prevCasesList.length - 1]?.caseReferenceId || '',
              caseBusinessVertical: prevCasesList[prevCasesList.length - 1]?.businessVertical || ''
            })
          );
      }
    }
  };

  const onGoToNextCase = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CaseDetails_NavigationClick, {
      navigateType: SETTER_FUNCTION.NEXT
    });
    if (currentCaseIndex != undefined && casesList) {
      if (currentCaseIndex < casesList.length - 1) {
        // If current case index is less than the total cases length then increment the current case index
        dispatch(setCurrentCasePointer(currentCaseIndex + 1));
        navigate(
          interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
            selectedTab: 'overview',
            loanId: casesList[currentCaseIndex + 1]?.accountNumber || '',
            customerId: casesList[currentCaseIndex + 1]?.customerId || '',
            caseReferenceId: casesList[currentCaseIndex + 1]?.caseReferenceId || '',
            caseBusinessVertical: casesList[currentCaseIndex + 1]?.businessVertical || ''
          })
        );
      } else if (pagination.pageNo + 1 <= (pagination?.totalPages || 0)) {
        // If current case index is greater or equal and page no is less than total number of pages, get the next cases list
        getNextCasesList(currentCaseIndex);
        dispatch(setNextCasesToCurrent());
        nextCasesList &&
          navigate(
            interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
              selectedTab: 'overview',
              loanId: nextCasesList[0]?.accountNumber || '',
              customerId: nextCasesList[0]?.customerId || '',
              caseReferenceId: nextCasesList[0]?.caseReferenceId || '',
              caseBusinessVertical: nextCasesList[0]?.businessVertical || ''
            })
          );
      }
    }
  };

  const onJumpToPreviousList = () => {
    if (currentCasePointer != undefined && casesList?.[currentCasePointer]) {
      navigate(
        interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
          selectedTab: 'overview',
          loanId: casesList[currentCasePointer]?.accountNumber || '',
          customerId: casesList[currentCasePointer]?.customerId || '',
          caseReferenceId: casesList[currentCasePointer]?.caseReferenceId || '',
          caseBusinessVertical: casesList[currentCasePointer]?.businessVertical || ''
        })
      );
    } else {
      navigate(interpolatePathParams(APP_ROUTES.CASES.path, {}));
    }
  };

  if (currentCaseIndex !== -1) {
    return (
      <>
        <div className={styles.goToNextCaseContainer}>
          {currentCaseIndex != undefined && pagination.pageSize != undefined && (
            <Typography variant="p3" className={styles.nextCaseTextHeader}>
              Case{' '}
              <Typography variant="p3" as="span" className="font-semibold">
                {pagination.pageNo * pagination.pageSize + currentCaseIndex + 1}
              </Typography>{' '}
              of {pagination.totalElements}
            </Typography>
          )}
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="small"
              onClick={onGoToPreviousCase}
              disabled={disablePreviousButton}
              startAdornment={<ArrowLeftSolidIcon />}
            >
              Prev
            </Button>
            <Button
              variant="secondary"
              size="small"
              disabled={disableNextButton}
              onClick={onGoToNextCase}
              endAdornment={<ArrowRightSolidIcon />}
            >
              Next
            </Button>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className={cx(styles.goToNextCaseContainer, 'justify-between')}>
        <Typography variant="p3" className={styles.nextCaseTextHeader}>
          Go back to previous list
        </Typography>
        <Button
          variant="secondary"
          className={styles.nextCaseButton}
          size="small"
          onClick={onJumpToPreviousList}
          startAdornment={<ArrowLeftSolidIcon />}
        >
          Go back
        </Button>
      </div>
    </>
  );
};

export default NextPreviousCaseActions;
